import { useEffect, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, vars } from "@pack-it-up/ui-web";
import "./app/global.css.js";
import type { UserId } from "@pack-it-up/shared";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "./app/AppContext.js";
import { applyFaviconAccent } from "./app/favicon.js";
import { SessionProvider } from "./app/SessionProvider.js";
import { AppShell } from "./app/AppShell.js";
import { SignIn } from "./pages/auth/SignIn.js";
import { SignUp } from "./pages/auth/SignUp.js";
import { ForgotPassword } from "./pages/auth/ForgotPassword.js";
import { HomePage } from "./pages/home/HomePage.js";
import { TripDetailPage } from "./pages/trip/TripDetailPage.js";
import { TemplateDetailPage } from "./pages/TemplateDetail/TemplateDetailPage.js";
import { MyTripsPage } from "./pages/trips/MyTripsPage.js";
import { MyTemplatesPage } from "./pages/templates/MyTemplatesPage.js";
import { FriendsPage } from "./pages/friends/FriendsPage.js";
import { InvitesPage } from "./pages/invites/InvitesPage.js";
import { SettingsPage } from "./pages/settings/SettingsPage.js";
import { JoinPage } from "./pages/join/JoinPage.js";

const Splash = (): ReactNode => (
  <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: vars.color.textMuted }}>Loading…</div>
);

// Auth routes (signed out). Unknown paths fall back to sign-in. The join page is
// public (Doc 08 §4): a join link is most often opened by someone without a session —
// it offers anonymous view or sign-up-to-join instead of bouncing to sign-in.
const SignedOutRoutes = (): ReactNode => (
  <Routes>
    <Route path="/signin" element={<SignIn />} />
    <Route path="/signup" element={<SignUp />} />
    <Route path="/forgot" element={<ForgotPassword />} />
    <Route path="/join/:token" element={<JoinPage />} />
    <Route path="*" element={<Navigate to="/signin" replace />} />
  </Routes>
);

const HomeRoute = (): ReactNode => <HomePage />;

// App routes (signed in), wrapped in the session-store provider keyed to the user.
// Screens for most routes land in later T15 slices; the route table (Doc 10 §7) is
// here now so the shell is complete.
const SignedInApp = ({ userId, isAnonymous }: { userId: UserId; isAnonymous: boolean }): ReactNode => (
  <SessionProvider userId={userId}>
    <AppShell>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/trip/:id/:tab?" element={<TripDetailPage />} />
        <Route path="/template/:id/:tab?" element={<TemplateDetailPage />} />
        <Route path="/trips" element={<MyTripsPage />} />
        <Route path="/templates" element={<MyTemplatesPage />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/invites" element={<InvitesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/join/:token" element={<JoinPage />} />
        {/* Reachable while "signed in" as an anonymous session too (#228): the sidebar's
            "Anonymous" footer sends an anon user here to upgrade to a real account, same
            reasoning as the /join/:token duplication above. A real (non-anonymous) account
            must never land here — e.g. mid-signup, the moment the account is created this
            same route re-renders under SignedInApp, and it must fall through to Home rather
            than re-showing the sign-up form. */}
        <Route path="/signup" element={isAnonymous ? <SignUp /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  </SessionProvider>
);

// Root: applies the active theme and gates the auth stack vs the app stack off
// `authStore.status` (Doc 04, Doc 10 §7).
export const App = (): ReactNode => {
  const { auth, theme } = useStores();
  const mode = useStoreValue(theme, (s) => s.theme);
  const accentColor = useStoreValue(theme, (s) => s.accentColor);
  const status = useStoreValue(auth, (s) => s.status);
  const user = useStoreValue(auth, (s) => s.user);
  useEffect(() => applyFaviconAccent(accentColor), [accentColor]);
  return (
    <ThemeProvider mode={mode} accentColor={accentColor}>
      {status === "loading" ? (
        <Splash />
      ) : status === "signedIn" && user !== null ? (
        <SignedInApp userId={user.id} isAnonymous={user.isAnonymous} />
      ) : (
        <SignedOutRoutes />
      )}
    </ThemeProvider>
  );
};
